import { useState, useEffect, useRef, memo, useCallback, useMemo } from "react"
import { AgentsPanel, type AgentsPanelHandle } from "@/components/agents/agents-panel"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Bell,
  Loader2,
  X,
  Trash2,
  Check,
  XCircle,
  Filter,
  Bot,
  Cpu,
  Zap,
  FileText,
  Share2,
  Download,
  Search,
  Hash,
  Plus,
  CheckCircle2,
  Clock,
  Users,
  DollarSign,
  Heart,
  TrendingUp,
  Target,
  Activity,
  TrendingDown,
  BarChart3,
  PieChart,
  LineChart,
  MessageSquare,
  Mail,
  Phone,
  List,
  Info,
} from "lucide-react"
import { useOrganization } from "@/contexts/organization-context"
import { useAuth } from "@/contexts/auth-context"
import { useNavigate } from "react-router-dom"
import {
  notificationsService,
  type Notification,
  AGENT_NOTIFICATION_KINDS,
  isAgentNotification,
  isHandsUpNotification,
  isResolvedHandsUpNotification,
  isApprovedHandsUpNotification,
  isControlSystemsNotification,
  isOptimizationNotification,
  isAgentReportNotification,
  getNotificationKindColor,
  getNotificationKindLabel,
  extractHandsUpId,
  extractAgentId,
  extractResolutionInfo,
} from "@/services/notifications"
import { workspaceService, type Workspace } from "@/services/workspaces"
import { agentsService, type AgentExecution, type Agent, type ExecutionByCycleResponse } from "@/services/agents"
import { api } from "@/services/api"
import { config } from "@/config/environment"
import { toast } from "sonner"
import ReactMarkdown from "react-markdown"
import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

// Icon map for agent icons (same as agents.tsx)
const agentIconMap: Record<string, typeof Users> = {
  "dollar-sign": DollarSign,
  "users": Users,
  "heart": Heart,
  "trending-up": TrendingUp,
  "target": Target,
  "zap": Zap,
  "trending-down": TrendingDown,
  "bar-chart": BarChart3,
  "pie-chart": PieChart,
  "line-chart": LineChart,
  "activity": Activity,
  "message": MessageSquare,
  "mail": Mail,
  "phone": Phone,
  "retention": Heart,
  "monetization": DollarSign,
}

// Memoized notification list item - only re-renders when its props change
const NotificationListItem = memo(function NotificationListItem({
  notification,
  isSelected,
  onSelect,
  icon,
  title,
  previewText,
  formattedDate,
  kindColor,
  kindLabel,
  isHandsUpItem,
  isCritical,
}: {
  notification: Notification
  isSelected: boolean
  onSelect: (notification: Notification) => void
  icon: React.ReactNode
  title: string
  previewText: string
  formattedDate: string
  kindColor: string
  kindLabel: string
  isHandsUpItem: boolean
  isCritical: boolean
}) {
  return (
    <button
      onClick={() => onSelect(notification)}
      className={cn(
        "flex flex-col gap-1 border-b p-4 text-left text-sm transition-colors hover:bg-muted/50 w-full overflow-hidden",
        isSelected && "bg-muted",
        isCritical && !isSelected && "bg-red-50/50",
        isHandsUpItem && !isCritical && !isSelected && "bg-orange-50/30"
      )}
    >
      <div className="flex w-full items-center gap-2" style={{ minWidth: 0 }}>
        <span className="shrink-0">{icon}</span>
        <span className="font-medium truncate" style={{ flex: '1 1 0%', minWidth: 0 }}>{title}</span>
        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
          {formattedDate}
        </span>
      </div>
      {notification.origin && (
        <span className="text-xs text-muted-foreground truncate block">
          {notification.origin}
        </span>
      )}
      <span className="text-xs text-muted-foreground line-clamp-3 text-left" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
        {previewText}
      </span>
      {isHandsUpItem && (
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
            Action Required
          </Badge>
        </div>
      )}
      {notification.kind === 'agent_hands_up_approved' && (
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
            Approved
          </Badge>
        </div>
      )}
      {notification.kind === 'agent_hands_up_rejected' && (
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
            Rejected
          </Badge>
        </div>
      )}
    </button>
  )
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  return (
    prevProps.notification.id === nextProps.notification.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.title === nextProps.title &&
    prevProps.previewText === nextProps.previewText &&
    prevProps.formattedDate === nextProps.formattedDate &&
    prevProps.kindLabel === nextProps.kindLabel &&
    prevProps.isHandsUpItem === nextProps.isHandsUpItem &&
    prevProps.isCritical === nextProps.isCritical
  )
})

export function NotificationsPage() {
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const agentsPanelRef = useRef<AgentsPanelHandle>(null)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const [channelToDelete, setChannelToDelete] = useState<{ id: number; name: string } | null>(null)
  const [deletingChannel, setDeletingChannel] = useState(false)
  const [slackDialogOpen, setSlackDialogOpen] = useState(false)
  const [kindFilter, setKindFilter] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [resolvingId, setResolvingId] = useState<number | null>(null)
  const [creatingShareLink, setCreatingShareLink] = useState<number | null>(null)
  const [executionDetails, setExecutionDetails] = useState<AgentExecution | ExecutionByCycleResponse | null>(null)
  const [executionLoading, setExecutionLoading] = useState(false)
  const [agentsMap, setAgentsMap] = useState<Map<number, Agent>>(new Map())
  const [agentsMapBySlug, setAgentsMapBySlug] = useState<Map<string, Agent>>(new Map())
  const [showMobileList, setShowMobileList] = useState(false)
  const [contentLoading, setContentLoading] = useState(false)
  const [displayedNotification, setDisplayedNotification] = useState<Notification | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [totalNotifications, setTotalNotifications] = useState(0)

  // Handle notification selection with deferred content loading
  const handleSelectNotification = useCallback((notification: Notification) => {
    // Immediately update selection for list highlighting
    setSelectedNotification(notification)
    setContentLoading(true)

    // Defer heavy content rendering to next tick
    setTimeout(() => {
      setDisplayedNotification(notification)
      setContentLoading(false)
    }, 0)
  }, [])

  // Mobile version also closes the dialog
  const handleSelectNotificationMobile = useCallback((notification: Notification) => {
    handleSelectNotification(notification)
    setShowMobileList(false)
  }, [handleSelectNotification])

  const slackAuthUrl = currentOrganization?.id
    ? `https://slack.com/oauth/v2/authorize?client_id=1507242961315.2360047789233&scope=channels:history,channels:read,chat:write,commands,files:write,groups:read,mpim:read,team:read,users.profile:read,users:read,incoming-webhook&user_scope=identity.basic&state=${currentOrganization.id}`
    : "https://slack.com/oauth/v2/authorize?client_id=1507242961315.2360047789233&scope=channels:history,channels:read,chat:write,commands,files:write,groups:read,mpim:read,team:read,users.profile:read,users:read,incoming-webhook&user_scope=identity.basic"

  useEffect(() => {
    if (currentOrganization?.id) {
      loadData()
    }
  }, [currentOrganization?.id])

  const loadData = async () => {
    if (!currentOrganization?.id) return

    setLoading(true)
    try {
      // Load notifications, workspaces, and agents in parallel
      const [notificationsResponse, workspacesData, agentsData] = await Promise.all([
        notificationsService.getNotifications(currentOrganization.id, { limit: 50, offset: 0 }),
        workspaceService.getWorkspaceChannels(currentOrganization.id),
        agentsService.getAgents(currentOrganization.id)
      ])

      // Handle both paginated response and legacy array response
      const notificationsList = notificationsResponse.notifications ?? []
      const hasMoreData = notificationsResponse.has_more ?? false
      const total = notificationsResponse.total ?? notificationsList.length

      setNotifications(notificationsList)
      setHasMore(hasMoreData)
      setTotalNotifications(total)
      setWorkspaces(workspacesData.workspaces)

      // Create maps of agent ID and slug to agent for quick lookup
      const mapById = new Map<number, Agent>()
      const mapBySlug = new Map<string, Agent>()
      agentsData.forEach(agent => {
        mapById.set(agent.id, agent)
        if (agent.slug) {
          mapBySlug.set(agent.slug, agent)
        }
      })
      setAgentsMap(mapById)
      setAgentsMapBySlug(mapBySlug)

      // Auto-select first notification if none selected
      if (notificationsList.length > 0 && !selectedNotification) {
        setSelectedNotification(notificationsList[0])
        setDisplayedNotification(notificationsList[0])
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  // Lightweight refresh of the recommendations list (no full-page loading flash).
  // Called when an agent run finishes so its new analysis appears without a reload.
  const refreshRecommendations = async () => {
    if (!currentOrganization?.id) return
    try {
      const response = await notificationsService.getNotifications(
        currentOrganization.id,
        { limit: 50, offset: 0 }
      )
      const list = response.notifications ?? []
      setNotifications(list)
      setHasMore(response.has_more ?? false)
      setTotalNotifications(response.total ?? list.length)
    } catch (error) {
      console.error('Error refreshing recommendations:', error)
    }
  }

  // Load more notifications for endless scroll
  const loadMore = useCallback(async () => {
    if (!currentOrganization?.id || loadingMore || !hasMore) return

    setLoadingMore(true)
    try {
      const response = await notificationsService.getNotifications(
        currentOrganization.id,
        { limit: 50, offset: notifications.length }
      )
      // Handle both paginated response and legacy array response
      const newNotifications = response.notifications ?? []
      setNotifications(prev => [...prev, ...newNotifications])
      setHasMore(response.has_more ?? false)
      setTotalNotifications(response.total ?? notifications.length + newNotifications.length)
    } catch (error) {
      console.error('Error loading more notifications:', error)
      toast.error('Failed to load more notifications')
    } finally {
      setLoadingMore(false)
    }
  }, [currentOrganization?.id, loadingMore, hasMore, notifications.length])


  // Fetch execution details when an agent notification is selected
  useEffect(() => {
    const fetchExecutionDetails = async () => {
      if (!selectedNotification || !isAgentNotification(selectedNotification.kind)) {
        setExecutionDetails(null)
        return
      }

      setExecutionLoading(true)
      try {
        // Try new format first: {agent_slug}_cycle_{cycle_number}
        const agentSlug = extractAgentSlug(selectedNotification.origin)
        const cycleNumber = extractCycleNumber(selectedNotification.origin)

        console.log('🔍 Fetching execution details:', {
          origin: selectedNotification.origin,
          agentSlug,
          cycleNumber,
        })

        if (agentSlug && cycleNumber) {
          console.log(`📡 Calling getExecutionByCycle(${agentSlug}, ${cycleNumber})`)
          const execution = await agentsService.getExecutionByCycle(agentSlug, cycleNumber)
          console.log('✅ Execution fetched:', execution)
          setExecutionDetails(execution)
          return
        }

        // Fallback to old format: execution_{id}
        const executionId = extractExecutionId(selectedNotification.origin)
        console.log('🔍 Trying old format, executionId:', executionId)
        if (executionId) {
          const execution = await agentsService.getExecution(executionId)
          console.log('✅ Execution fetched (old format):', execution)
          setExecutionDetails(execution)
          return
        }

        // No execution ID found in either format
        console.log('⚠️ No execution ID found in origin')
        setExecutionDetails(null)
      } catch (error) {
        console.error('❌ Error fetching execution details:', error)
        setExecutionDetails(null)
      } finally {
        setExecutionLoading(false)
      }
    }

    fetchExecutionDetails()
  }, [selectedNotification?.id])

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown'

    const date = new Date(dateString)
    const now = new Date()

    const isToday = date.toDateString() === now.toDateString()

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    }

    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const formatFullDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown date'

    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const handleDeleteChannel = async () => {
    if (!channelToDelete || !currentOrganization?.id) return

    setDeletingChannel(true)
    try {
      await workspaceService.deleteChannel(channelToDelete.id, currentOrganization.id)
      toast.success(`Channel #${channelToDelete.name} removed successfully`)

      // Reload the workspace data
      await loadData()
      setChannelToDelete(null)
    } catch (error) {
      console.error('Error deleting channel:', error)
      toast.error('Failed to remove channel')
    } finally {
      setDeletingChannel(false)
    }
  }

  // Find agent by origin (supports both old agent_{id} and new {slug}_cycle_{num} formats)
  const findAgentByOrigin = (origin: string | null): Agent | null => {
    if (!origin) return null

    // Try new format first: {agent_slug}_cycle_{cycle_number}
    const slugMatch = origin.match(/^(.+)_cycle_\d+$/)
    if (slugMatch) {
      const slug = slugMatch[1]
      return agentsMapBySlug.get(slug) || null
    }

    // Try old format: agent_{id}_...
    const agentId = extractAgentId(origin)
    return agentId ? agentsMap.get(agentId) || null : null
  }

  // Get notification title based on kind
  const getNotificationTitle = (notification: Notification) => {
    if (isAgentReportNotification(notification.kind)) {
      const agent = findAgentByOrigin(notification.origin)
      return agent?.name || 'System Analysis'
    }
    if (isAgentNotification(notification.kind)) return 'Agent Notification'
    if (isControlSystemsNotification(notification.kind)) return 'Control Systems'
    if (isOptimizationNotification(notification.kind)) return 'Optimization Recommendation'
    return notification.kind || 'Notification'
  }

  // Filter notifications by kind and search
  const filteredNotifications = notifications.filter(n => {
    // Kind filter
    if (kindFilter) {
      if (kindFilter === 'agent' && !isAgentNotification(n.kind)) return false
      if (kindFilter === 'hands_up' && !isHandsUpNotification(n.kind) && !isResolvedHandsUpNotification(n.kind)) return false
      if (kindFilter === 'control_systems' && !isControlSystemsNotification(n.kind)) return false
      if (kindFilter === 'optimization' && !isOptimizationNotification(n.kind)) return false
      if (!['agent', 'hands_up', 'control_systems', 'optimization'].includes(kindFilter) && n.kind !== kindFilter) return false
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const matchesTitle = getNotificationTitle(n).toLowerCase().includes(query)
      const matchesContent = (n.notification || '').toLowerCase().includes(query)
      const matchesOrigin = (n.origin || '').toLowerCase().includes(query)
      if (!matchesTitle && !matchesContent && !matchesOrigin) return false
    }

    return true
  })

  // Count notifications by type for filter badges
  const notificationCounts = {
    all: notifications.length,
    agent: notifications.filter(n => isAgentNotification(n.kind)).length,
    hands_up: notifications.filter(n => isHandsUpNotification(n.kind) || isResolvedHandsUpNotification(n.kind)).length,
    control_systems: notifications.filter(n => isControlSystemsNotification(n.kind)).length,
    optimization: notifications.filter(n => isOptimizationNotification(n.kind)).length,
  }

  // Handle approve/reject for hands-up notifications
  const handleResolveHandsUp = async (
    notification: Notification,
    status: 'approved' | 'rejected'
  ) => {
    // Extract hands-up ID from notification URL (format: /agents/{slug}/hands-up/{id})
    const urlMatch = notification.url?.match(/\/hands-up\/(\d+)/)
    const handsUpId = urlMatch ? parseInt(urlMatch[1], 10) : null

    if (!handsUpId) {
      toast.error('Could not find hands-up ID')
      return
    }

    setResolvingId(notification.id)
    try {
      await notificationsService.resolveHandsUp(
        0, // agentId not used by API
        handsUpId,
        { status },
        user?.email || 'unknown'
      )
      toast.success(`Recommendation ${status}`)
      // Reload notifications to show updated status
      await loadData()
    } catch (error) {
      console.error('Error resolving hands-up:', error)
      toast.error(`Failed to ${status} recommendation`)
    } finally {
      setResolvingId(null)
    }
  }

  // Navigate to execution details
  const handleViewDetails = (notification: Notification) => {
    if (!currentOrganization?.slug) return

    // Try new format first: {agent_slug}_cycle_{cycle_number}
    if (notification.origin && isAgentNotification(notification.kind)) {
      const agentSlug = extractAgentSlug(notification.origin)
      const cycleNumber = extractCycleNumber(notification.origin)

      if (agentSlug && cycleNumber) {
        // New URL format: /optimizer/agent/{agent_slug}/executions?cycle_number={cycle_number}
        navigate(`/${currentOrganization.slug}/optimizer/agent/${agentSlug}/executions?cycle_number=${cycleNumber}`)
        return
      }

      // Try old format: agent_{id}_execution_{id}
      const agentId = extractAgentId(notification.origin)
      const executionMatch = notification.origin.match(/execution_(\d+)/)
      const executionId = executionMatch ? parseInt(executionMatch[1], 10) : null

      if (agentId && executionId) {
        navigate(`/${currentOrganization.slug}/agents/${agentId}/executions/${executionId}`)
        return
      }
    }

    // Fallback: use notification.url with org slug prefix
    if (notification.url) {
      // URL from backend is like /agents/98/executions/123, prepend org slug
      navigate(`/${currentOrganization.slug}${notification.url}`)
    }
  }

  // Extract execution ID from notification origin
  // Supports both old format (agent_{id}_execution_{id}) and new format ({agent_slug}_cycle_{cycle_number})
  const extractExecutionId = (origin: string | null): number | null => {
    if (!origin) return null
    // Try old format first: execution_123
    const executionMatch = origin.match(/execution_(\d+)/)
    if (executionMatch) return parseInt(executionMatch[1], 10)
    // New format doesn't have execution ID directly, return null
    // The cycle_number is used differently in the new architecture
    return null
  }

  // Extract cycle number from notification origin (new format: {agent_slug}_cycle_{cycle_number})
  const extractCycleNumber = (origin: string | null): number | null => {
    if (!origin) return null
    const cycleMatch = origin.match(/cycle_(\d+)/)
    return cycleMatch ? parseInt(cycleMatch[1], 10) : null
  }

  // Extract agent slug from notification origin (new format: {agent_slug}_cycle_{cycle_number})
  const extractAgentSlug = (origin: string | null): string | null => {
    if (!origin) return null
    // New format: agent_slug_cycle_123
    const match = origin.match(/^(.+)_cycle_\d+$/)
    return match ? match[1] : null
  }

  // Handle PDF download
  const handleDownloadPDF = async (notification: Notification) => {
    let executionId = extractExecutionId(notification.origin)

    // If old format didn't work, try new format with agent_slug + cycle_number
    if (!executionId) {
      const agentSlug = extractAgentSlug(notification.origin)
      const cycleNumber = extractCycleNumber(notification.origin)

      if (agentSlug && cycleNumber) {
        try {
          const execution = await agentsService.getExecutionByCycle(agentSlug, cycleNumber)
          executionId = execution.id
        } catch (error) {
          console.error('Error fetching execution by cycle:', error)
          toast.error('Could not find execution data')
          return
        }
      }
    }

    if (!executionId) {
      toast.error('Could not find execution ID')
      return
    }
    // Open PDF export endpoint in new tab for printing
    // Use /api proxy which routes through gateway to agents service
    window.open(`${config.apiBaseUrl}/exports/${executionId}/pdf`, '_blank')
  }

  // Handle share link creation
  const handleCreateShareLink = async (notification: Notification) => {
    let executionId = extractExecutionId(notification.origin)

    // If old format didn't work, try new format with agent_slug + cycle_number
    if (!executionId) {
      const agentSlug = extractAgentSlug(notification.origin)
      const cycleNumber = extractCycleNumber(notification.origin)

      if (agentSlug && cycleNumber) {
        try {
          const execution = await agentsService.getExecutionByCycle(agentSlug, cycleNumber)
          executionId = execution.id
        } catch (error) {
          console.error('Error fetching execution by cycle:', error)
          toast.error('Could not find execution data')
          return
        }
      }
    }

    if (!executionId) {
      toast.error('Could not find execution ID')
      return
    }

    setCreatingShareLink(notification.id)
    try {
      // Use api service which routes through gateway to agents service
      // Backend expects created_by as query parameter, not body
      const createdBy = encodeURIComponent(user?.email || 'unknown')
      const response = await api.post<{ share_url: string }>(`/exports/${executionId}/share?created_by=${createdBy}`)

      // Try to copy share URL to clipboard
      try {
        await navigator.clipboard.writeText(response.data.share_url)
        toast.success('Share link copied to clipboard! Link expires in 24 hours.')
      } catch (clipboardError) {
        // Clipboard failed (common on mobile), show clickable link in toast
        console.warn('Clipboard access denied, showing link in toast:', clipboardError)
        toast.success(
          <p>
            Share{' '}
            <a
              href={response.data.share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium"
            >
              link
            </a>
            {' '}created! Expires in 24 hours.
          </p>,
          { duration: 10000 }
        )
      }
    } catch (error: any) {
      console.error('Error creating share link:', error)
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error'
      toast.error(`Failed to create share link: ${detail}`)
    } finally {
      setCreatingShareLink(null)
    }
  }

  // Handle support data download - opens the CSV file from GCS
  const handleDownloadData = async (notification: Notification) => {
    const agentSlug = extractAgentSlug(notification.origin)
    const cycleNumber = extractCycleNumber(notification.origin)

    if (!agentSlug || !cycleNumber) {
      toast.error('Could not find execution data')
      return
    }

    try {
      // Get full execution details including decision data
      const execution = await agentsService.getExecutionByCycle(agentSlug, cycleNumber)

      // CSV is stored in GCS, signed URL in decision.data_context.file_url
      const fileUrl = execution.decision?.data_context?.file_url
      if (fileUrl) {
        try {
          const response = await fetch(fileUrl)
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${agentSlug}_cycle_${cycleNumber}.csv`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        } catch {
          // Fallback: open directly if fetch fails (e.g. CORS)
          window.open(fileUrl, '_blank')
        }
        toast.success('Data exported successfully')
        return
      }

      // Fallback: inline data rows (legacy executions)
      const inputs = execution.decision?.data_context?.data
      if (!inputs || !Array.isArray(inputs) || inputs.length === 0) {
        toast.error('No input data available for this execution (CSV link may have expired)')
        return
      }

      // Convert data rows to CSV
      const headers = Object.keys(inputs[0])
      const csvRows = [
        headers.join(','),
        ...inputs.map(row =>
          headers.map(header => {
            const value = row[header]
            if (value === null || value === undefined) return ''
            const stringValue = String(value)
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`
            }
            return stringValue
          }).join(',')
        )
      ]
      const csvContent = csvRows.join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${agentSlug}_cycle_${cycleNumber}_data.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success('Data exported successfully')
    } catch (error: any) {
      console.error('Error downloading data:', error)
      toast.error('Failed to export data')
    }
  }

  // Get notification icon based on kind and agent
  const getNotificationIcon = (notification: Notification) => {
    // For agent notifications, try to get the agent's custom icon and color
    if (isAgentNotification(notification.kind)) {
      const agent = findAgentByOrigin(notification.origin)

      if (agent) {
        const iconKey = (agent.icon?.trim() || 'users') as keyof typeof agentIconMap
        const IconComponent = agentIconMap[iconKey] || Users
        const bgColor = agent.color?.startsWith('#') ? agent.color : '#3b82f6'

        return (
          <div
            className="flex h-5 w-5 items-center justify-center rounded"
            style={{ backgroundColor: bgColor }}
          >
            <IconComponent className="h-3 w-3 text-white" />
          </div>
        )
      }

      // Fallback to Bot icon with default color
      const color = isHandsUpNotification(notification.kind) ? '#f97316' : '#3b82f6'
      return <Bot className="h-4 w-4" style={{ color }} />
    }

    if (isControlSystemsNotification(notification.kind)) return <Cpu className="h-4 w-4 text-purple-500" />
    if (isOptimizationNotification(notification.kind)) return <Zap className="h-4 w-4 text-green-500" />
    return <Bell className="h-4 w-4 text-blue-500" />
  }

  // Get preview text from notification
  const getPreviewText = (notification: Notification): string => {
    const message = notification.notification || ''
    // Try to extract executive summary first
    const executiveSummaryMatch = message.match(/##\s*Executive Summary\s*([\s\S]*?)(?=\n##|$)/i)
    if (executiveSummaryMatch) {
      return executiveSummaryMatch[1].trim().substring(0, 150)
    }
    // For agent reports, skip the title (first line) and start from the content
    if (isAgentNotification(notification.kind)) {
      const lines = message.split('\n').filter(line => line.trim())
      // Skip first line if it looks like a title (short, no period at end)
      if (lines.length > 1 && lines[0].length < 100 && !lines[0].trim().endsWith('.')) {
        const content = lines.slice(1).join(' ').replace(/[#*`_\[\]]/g, '').trim()
        return content.substring(0, 150)
      }
    }
    // Strip markdown and return first 150 chars
    return message.replace(/[#*`_\[\]]/g, '').substring(0, 150)
  }

  // Pre-compute notification data to avoid recalculating on every render
  const computedNotifications = useMemo(() => {
    return filteredNotifications.map(notification => ({
      notification,
      icon: getNotificationIcon(notification),
      title: getNotificationTitle(notification),
      previewText: getPreviewText(notification),
      formattedDate: formatDate(notification.created_at),
      kindColor: getNotificationKindColor(notification.kind),
      kindLabel: getNotificationKindLabel(notification.kind),
      isHandsUpItem: isHandsUpNotification(notification.kind),
      isCritical: notification.kind === AGENT_NOTIFICATION_KINDS.HANDS_UP_CRITICAL,
    }))
  }, [filteredNotifications, agentsMap, agentsMapBySlug])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const isHandsUp = selectedNotification ? isHandsUpNotification(selectedNotification.kind) : false
  const isResolvedHandsUp = selectedNotification ? isResolvedHandsUpNotification(selectedNotification.kind) : false
  const isApprovedHandsUp = selectedNotification ? isApprovedHandsUpNotification(selectedNotification.kind) : false
  const resolutionInfo = selectedNotification ? extractResolutionInfo(selectedNotification.origin) : null
  const isAgentReport = selectedNotification ? isAgentReportNotification(selectedNotification.kind) : false
  const isResolving = selectedNotification ? resolvingId === selectedNotification.id : false

  return (
    <div className="flex flex-col gap-6">
      {/* Header (title + subtitle) */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Agents</h2>
          <p className="text-slate-600">
            AI-powered insights to optimize your business goals
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Agents actions (trigger the panel's dialogs) */}
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => agentsPanelRef.current?.openAIConfig()}
          >
            <Zap className="h-4 w-4 mr-1" />
            AI Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => agentsPanelRef.current?.openAddDialog()}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Agent
          </Button>
          {/* Mobile: Button to open list */}
          <Button
            variant="outline"
            size="sm"
            className="md:hidden"
            onClick={() => setShowMobileList(true)}
          >
            <List className="h-4 w-4 mr-2" />
            {filteredNotifications.length} recommendations
          </Button>
        </div>
      </div>

      {/* Agents panel (extracted from the Optimizer page) — below the title/subtitle */}
      <AgentsPanel ref={agentsPanelRef} onRunComplete={refreshRecommendations} />

      {/* Recommendations */}
      <div className="flex h-[calc(100vh-8rem)] flex-col gap-4">
      {/* Two-panel layout */}
      <div className="flex flex-1 overflow-hidden rounded-lg border bg-background">
        <ResizablePanelGroup direction="horizontal">
        {/* Left Panel - Notification List (hidden on mobile) */}
        <ResizablePanel defaultSize="30%" minSize="15%" maxSize="50%" className="hidden md:flex flex-col">
          {/* List Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <h2 className="text-sm font-medium text-muted-foreground">
              {kindFilter || searchQuery
                ? `${filteredNotifications.length} recommendations`
                : totalNotifications > notifications.length
                  ? `${notifications.length} of ${totalNotifications} recommendations`
                  : `${filteredNotifications.length} recommendations`}
            </h2>
            <div className="flex items-center gap-1">
              {/* Filter dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Filter className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setKindFilter(null)}>
                    <span className="flex-1">All recommendations</span>
                    <Badge variant="secondary" className="ml-2">{notificationCounts.all}</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setKindFilter('control_systems')}>
                    <Cpu className="h-4 w-4 mr-2 text-purple-500" />
                    <span className="flex-1">Control Systems</span>
                    <Badge variant="secondary" className="ml-2">{notificationCounts.control_systems}</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setKindFilter('optimization')}>
                    <Zap className="h-4 w-4 mr-2 text-green-500" />
                    <span className="flex-1">Optimization</span>
                    <Badge variant="secondary" className="ml-2">{notificationCounts.optimization}</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setKindFilter('agent')}>
                    <Bot className="h-4 w-4 mr-2 text-blue-500" />
                    <span className="flex-1">Agent</span>
                    <Badge variant="secondary" className="ml-2">{notificationCounts.agent}</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setKindFilter('hands_up')}>
                    <Bell className="h-4 w-4 mr-2 text-orange-500" />
                    <span className="flex-1">Action Required</span>
                    <Badge variant="secondary" className="ml-2">{notificationCounts.hands_up}</Badge>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Slack button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {workspaces.length > 0 ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 relative"
                        onClick={() => setSlackDialogOpen(true)}
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                        </svg>
                        <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                          {workspaces.reduce((acc, w) => acc + w.channels.length, 0)}
                        </span>
                      </Button>
                    ) : (
                      <a href={slackAuthUrl} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                          </svg>
                        </Button>
                      </a>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {workspaces.length > 0 ? 'Manage Slack channels' : 'Add to Slack'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search recommendations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>

          {/* Active filter indicator */}
          {kindFilter && (
            <div className="px-4 py-2 border-b bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Filtered: {kindFilter === 'agent' ? 'Agent' :
                    kindFilter === 'hands_up' ? 'Action Required' :
                    kindFilter === 'control_systems' ? 'Control Systems' :
                    kindFilter === 'optimization' ? 'Optimization' : kindFilter}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setKindFilter(null)} className="h-6 px-2">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col">
              {computedNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Bell className="h-8 w-8 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {kindFilter ? 'No notifications match this filter' : 'No notifications yet'}
                  </p>
                </div>
              ) : (
                <>
                  {computedNotifications.map((item) => (
                    <NotificationListItem
                      key={item.notification.id}
                      notification={item.notification}
                      isSelected={selectedNotification?.id === item.notification.id}
                      onSelect={handleSelectNotification}
                      icon={item.icon}
                      title={item.title}
                      previewText={item.previewText}
                      formattedDate={item.formattedDate}
                      kindColor={item.kindColor}
                      kindLabel={item.kindLabel}
                      isHandsUpItem={item.isHandsUpItem}
                      isCritical={item.isCritical}
                    />
                  ))}
                  {/* Load more button */}
                  {hasMore && (
                    <div className="flex items-center justify-center py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="text-xs text-muted-foreground"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          `Load more (${totalNotifications - notifications.length} remaining)`
                        )}
                      </Button>
                    </div>
                  )}
                  {!hasMore && notifications.length > 0 && (
                    <div className="text-center py-3 text-xs text-muted-foreground">
                      {totalNotifications} total recommendations
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle className="hidden md:flex" />

        {/* Right Panel - Notification Detail */}
        <ResizablePanel defaultSize="70%" className="flex flex-col min-w-0">
          {selectedNotification ? (
            <>
              {/* Detail Header */}
              <div className="p-4 border-b space-y-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {getNotificationIcon(selectedNotification)}
                      <h2 className="text-lg font-semibold truncate">
                        {getNotificationTitle(selectedNotification)}
                      </h2>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {formatFullDate(selectedNotification.created_at)}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-wrap md:ml-4">
                    {isHandsUp && (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleResolveHandsUp(selectedNotification, 'approved')}
                          disabled={isResolving}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {isResolving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResolveHandsUp(selectedNotification, 'rejected')}
                          disabled={isResolving}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                    {isResolvedHandsUp && resolutionInfo && (
                      <div className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm",
                        isApprovedHandsUp ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                      )}>
                        {isApprovedHandsUp ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        <span>
                          {isApprovedHandsUp ? 'Approved' : 'Rejected'} by {resolutionInfo.resolvedBy}
                        </span>
                        <span className="text-muted-foreground">
                          {new Date(resolutionInfo.resolvedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                    {isAgentReport && selectedNotification.origin && (
                      <>
                        {executionLoading && (
                          <Button variant="outline" size="sm" disabled>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Loading...
                          </Button>
                        )}
                        {!executionLoading && executionDetails && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Info className="h-4 w-4 mr-1" />
                                Execution
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                              <div className="px-3 py-2 border-b">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                  <span>{'cycle_number' in executionDetails ? `Cycle #${executionDetails.cycle_number}` : `Execution #${executionDetails.id}`}</span>
                                  <span className={cn(
                                    "flex items-center gap-1 text-xs",
                                    executionDetails.status === 'completed' && "text-emerald-600",
                                    executionDetails.status === 'failed' && "text-red-600",
                                    executionDetails.status === 'running' && "text-blue-600",
                                    executionDetails.status === 'cancelled' && "text-amber-600"
                                  )}>
                                    {executionDetails.status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
                                    {executionDetails.status === 'failed' && <XCircle className="h-3 w-3" />}
                                    {executionDetails.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {executionDetails.status === 'cancelled' && <Clock className="h-3 w-3" />}
                                    <span className="capitalize">{executionDetails.status}</span>
                                  </span>
                                </div>
                              </div>
                              <div className="p-2 space-y-1 text-xs">
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Model</span>
                                  <span className="font-mono">{('llm_model_used' in executionDetails ? executionDetails.llm_model_used : executionDetails.llm_model) || '-'}</span>
                                </div>
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Tokens</span>
                                  <span>{executionDetails.tokens_input?.toLocaleString() || 0} in / {executionDetails.tokens_output?.toLocaleString() || 0} out</span>
                                </div>
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Cost</span>
                                  <span>${executionDetails.llm_cost_usd?.toFixed(4) || '0.0000'}</span>
                                </div>
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Effectiveness</span>
                                  <span>
                                    {'effectiveness' in executionDetails && executionDetails.effectiveness !== null
                                      ? `${(executionDetails.effectiveness * 100).toFixed(0)}%`
                                      : '-'}
                                  </span>
                                </div>
                                {'objectives' in executionDetails && executionDetails.objectives && (() => {
                                  const obj = executionDetails.objectives
                                  // New format: {objectives: [{metric, operator, target}], ...}
                                  if (obj.objectives && Array.isArray(obj.objectives) && obj.objectives.length > 0) {
                                    return (
                                      <div className="flex justify-between py-1">
                                        <span className="text-muted-foreground">Goal</span>
                                        <span className="text-right">
                                          {obj.objectives.map((o: any, i: number) => (
                                            <span key={i}>
                                              {i > 0 && ', '}
                                              {o.metric?.replace(/_/g, ' ')} {o.operator || '≥'} {o.target != null ? Number(o.target).toLocaleString() : '?'}
                                            </span>
                                          ))}
                                        </span>
                                      </div>
                                    )
                                  }
                                  // Legacy format: {objective_type, primary_metric, target_value}
                                  if (obj.primary_metric) {
                                    return (
                                      <div className="flex justify-between py-1">
                                        <span className="text-muted-foreground">Goal</span>
                                        <span className="text-right">
                                          {obj.objective_type} {obj.primary_metric.replace(/_/g, ' ')}
                                          {obj.target_value != null && obj.target_value !== 0
                                            ? ` ≥ ${obj.target_value}`
                                            : ''}
                                        </span>
                                      </div>
                                    )
                                  }
                                  return null
                                })()}
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Duration</span>
                                  <span>{executionDetails.duration_ms ? `${(executionDetails.duration_ms / 1000).toFixed(1)}s` : '-'}</span>
                                </div>
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Trigger</span>
                                  <span className="capitalize">{executionDetails.trigger_type}</span>
                                </div>
                                <div className="flex justify-between py-1">
                                  <span className="text-muted-foreground">Completed</span>
                                  <span>{executionDetails.completed_at ? new Date(executionDetails.completed_at).toLocaleString() : '-'}</span>
                                </div>
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadData(selectedNotification)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Support
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCreateShareLink(selectedNotification)}
                          disabled={creatingShareLink === selectedNotification.id}
                        >
                          {creatingShareLink === selectedNotification.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Share2 className="h-4 w-4 mr-1" />
                              Share
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Detail Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {contentLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none [word-break:break-word] [overflow-wrap:break-word] [&_pre]:whitespace-pre-wrap [&_table]:block [&_table]:overflow-x-auto">
                    <ReactMarkdown
                      remarkPlugins={[remarkBreaks, remarkGfm]}
                    >
                      {displayedNotification?.notification || 'No content'}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a notification to read</p>
              </div>
            </div>
          )}
        </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Slack Channels Dialog */}
      <Dialog open={slackDialogOpen} onOpenChange={setSlackDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
              </svg>
              Slack Channels
            </DialogTitle>
            <DialogDescription>
              Notifications will be delivered to these channels
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {workspaces.map((workspace) => (
              <div key={workspace.workspace_id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="font-medium text-sm">{workspace.workspace_name}</span>
                </div>
                {workspace.channels.length > 0 ? (
                  <div className="space-y-1 pl-4">
                    {workspace.channels.map((channel) => (
                      <div
                        key={channel.id}
                        className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted group"
                      >
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{channel.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            setChannelToDelete({ id: channel.id, name: channel.name })
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground pl-4">No channels connected</p>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <a href={slackAuthUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Channel
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Channel Confirmation Dialog */}
      <AlertDialog open={!!channelToDelete} onOpenChange={(open) => !open && setChannelToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Slack Channel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <span className="font-semibold">#{channelToDelete?.name}</span> from receiving notifications?
              <br />
              <br />
              You can always add it back later by inviting the bot to the channel again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingChannel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChannel}
              disabled={deletingChannel}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deletingChannel ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Channel
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Notification List Dialog */}
      <Dialog open={showMobileList} onOpenChange={setShowMobileList}>
        <DialogContent className="max-w-[95vw] h-[85vh] p-0 flex flex-col">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span>
                {kindFilter || searchQuery
                  ? `${filteredNotifications.length} recommendations`
                  : totalNotifications > notifications.length
                    ? `${notifications.length} of ${totalNotifications}`
                    : `${filteredNotifications.length} recommendations`}
              </span>
              <div className="flex items-center gap-1 mr-8">
                {/* Filter dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Filter className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setKindFilter(null)}>
                      <span className="flex-1">All recommendations</span>
                      <Badge variant="secondary" className="ml-2">{notificationCounts.all}</Badge>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setKindFilter('control_systems')}>
                      <Cpu className="h-4 w-4 mr-2 text-purple-500" />
                      <span className="flex-1">Control Systems</span>
                      <Badge variant="secondary" className="ml-2">{notificationCounts.control_systems}</Badge>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setKindFilter('optimization')}>
                      <Zap className="h-4 w-4 mr-2 text-green-500" />
                      <span className="flex-1">Optimization</span>
                      <Badge variant="secondary" className="ml-2">{notificationCounts.optimization}</Badge>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setKindFilter('agent')}>
                      <Bot className="h-4 w-4 mr-2 text-blue-500" />
                      <span className="flex-1">Agent</span>
                      <Badge variant="secondary" className="ml-2">{notificationCounts.agent}</Badge>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setKindFilter('hands_up')}>
                      <Bell className="h-4 w-4 mr-2 text-orange-500" />
                      <span className="flex-1">Action Required</span>
                      <Badge variant="secondary" className="ml-2">{notificationCounts.hands_up}</Badge>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="px-4 py-2 border-b shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search recommendations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>

          {/* Active filter indicator */}
          {kindFilter && (
            <div className="px-4 py-2 border-b bg-muted/50 shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Filtered: {kindFilter === 'agent' ? 'Agent' :
                    kindFilter === 'hands_up' ? 'Action Required' :
                    kindFilter === 'control_systems' ? 'Control Systems' :
                    kindFilter === 'optimization' ? 'Optimization' : kindFilter}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setKindFilter(null)} className="h-6 px-2">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Notification List */}
          <ScrollArea className="flex-1">
            <div className="flex flex-col" style={{ maxWidth: '100%', overflow: 'hidden' }}>
              {computedNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Bell className="h-8 w-8 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {kindFilter ? 'No notifications match this filter' : 'No notifications yet'}
                  </p>
                </div>
              ) : (
                <>
                  {computedNotifications.map((item) => (
                    <NotificationListItem
                      key={item.notification.id}
                      notification={item.notification}
                      isSelected={selectedNotification?.id === item.notification.id}
                      onSelect={handleSelectNotificationMobile}
                      icon={item.icon}
                      title={item.title}
                      previewText={item.previewText}
                      formattedDate={item.formattedDate}
                      kindColor={item.kindColor}
                      kindLabel={item.kindLabel}
                      isHandsUpItem={item.isHandsUpItem}
                      isCritical={item.isCritical}
                    />
                  ))}
                  {/* Load more button for mobile */}
                  {hasMore && (
                    <div className="flex items-center justify-center py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="text-xs text-muted-foreground"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          `Load more (${totalNotifications - notifications.length} remaining)`
                        )}
                      </Button>
                    </div>
                  )}
                  {!hasMore && notifications.length > 0 && (
                    <div className="text-center py-3 text-xs text-muted-foreground">
                      {totalNotifications} total recommendations
                    </div>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  )
}
